// AUD-4 — daily audio-brief player hook (Phase 2, mobile surface).
//
// Owns a single HTMLAudioElement and drives playback of the latest `research_brief_episodes` row for
// the signed-in user. Built on the shipped offline substrate:
//   • the episode read goes through `useCachedQuery` (queryKey ["mobile-brief"], cacheKey
//     "mobile.brief_episode"), so last-known-good is written through to the persistent cache, a
//     synchronous cached placeholder paints before/without a network round-trip, and the page never
//     blanks offline. The queryFn THROWS on `{ error }` so React Query keeps the cached placeholder and
//     serves it while offline rather than overwriting it with null.
//   • The table is EMPTY until research-tts-prebrief runs (gated), so `null` is a valid, non-error
//     state — the page renders a friendly empty state, never an error.
//
// Playback URL: the audio lives in the PRIVATE `research-briefs` bucket at `audio_path`. We resolve a
// 1-hour signed URL via storage.createSignedUrl. On native we ALSO pre-download the bytes to the
// Filesystem (dynamic-imported behind isNative(), mirroring offlineQueue/useIsOnline) so the brief
// plays OFFLINE; on web we just use the signed URL. A native plugin is NEVER imported at module top
// level so the public/desktop bundle stays clean.
//
// navigator.mediaSession is wired (guarded) so Android lock-screen transport works while the app
// process is alive. AUD-5 (Android): a native foreground media service (BriefAudioService /
// BriefAudioServicePlugin) is started when brief playback begins and stopped when it ends/pauses, so the
// WebView keeps decoding while the app is backgrounded or the screen is locked. That bridge is gated
// behind isNative() with a dynamic import + try/catch, so the web/desktop bundle is unchanged and
// playback still works exactly as before when the plugin is absent. CarPlay (iOS) + a MediaBrowser
// service for Android Auto remain GATED follow-ups.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { removeCached } from '@/lib/offline/persistentCache';
import { isNative } from '@/lib/native/platform';

// ---------------------------------------------------------------------------
// Shapes (research_brief_episodes — stale generated types → cast at the call site)
// ---------------------------------------------------------------------------

export interface BriefChapter {
  title: string;
  start_sec: number;
  /**
   * Optional per-chapter blurb. A later research-tts-prebrief change will write this; until then it is
   * absent and the UI shows the title alone. Forward-compatible: we read it when present, never require it.
   */
  summary?: string | null;
}

export interface BriefEpisode {
  id: string;
  user_id: string | null;
  surface: string | null;
  target_date: string | null;
  audio_path: string | null;
  duration_sec: number | null;
  chapters: BriefChapter[];
  voice: string | null;
  vendor: string | null;
  region: string | null;
  generated_at: string | null;
}

const STORAGE_BUCKET = 'research-briefs';
const CACHE_KEY = 'mobile.brief_episode';
const QUERY_KEY = ['mobile-brief'] as const;

// Where the pre-downloaded audio lives on native (app-sandboxed Cache dir). Stable single-file path —
// each fresh episode overwrites the previous download. The `.m4a` extension is NOMINAL only: the
// webview sniffs the actual container from content, not the filename, and Capacitor.convertFileSrc
// ignores the extension — so this still plays correctly even if research-tts-prebrief emits mp3/ogg.
const BRIEF_DIR = 'cbam-brief';
const BRIEF_FILE = `${BRIEF_DIR}/brief_audio.m4a`;

// Normalise the chapters jsonb into a typed array, dropping malformed entries. Defensive: the column is
// opaque jsonb, so never assume it is well-formed.
function normaliseChapters(raw: unknown): BriefChapter[] {
  if (!Array.isArray(raw)) return [];
  const out: BriefChapter[] = [];
  for (const c of raw) {
    if (c && typeof c === 'object') {
      const obj = c as Record<string, unknown>;
      const title = typeof obj.title === 'string' ? obj.title : null;
      const start =
        typeof obj.start_sec === 'number'
          ? obj.start_sec
          : typeof obj.start_sec === 'string'
            ? Number(obj.start_sec)
            : null;
      if (title != null && start != null && Number.isFinite(start)) {
        // Forward-compat: a later edge-function change adds chapters[].summary. Read it when present
        // (string only), else leave it undefined so the UI shows the title alone.
        const summary = typeof obj.summary === 'string' && obj.summary.trim() ? obj.summary : undefined;
        out.push({ title, start_sec: start, summary });
      }
    }
  }
  // Keep chapters in start order so "current chapter" math and prev/next are monotonic.
  return out.sort((a, b) => a.start_sec - b.start_sec);
}

// Preferred surface order for tabs + default selection. 'comprehensive' is the default; the rest follow
// in the canonical cio → pm → analyst order. Any unknown surface sorts after these.
export const SURFACE_ORDER = ['comprehensive', 'cio', 'pm', 'analyst'] as const;

function surfaceRank(surface: string | null): number {
  if (!surface) return SURFACE_ORDER.length;
  const i = (SURFACE_ORDER as readonly string[]).indexOf(surface);
  return i === -1 ? SURFACE_ORDER.length : i;
}

// Map a raw row → typed BriefEpisode.
function mapEpisode(r: Record<string, unknown>): BriefEpisode {
  return {
    id: String(r.id),
    user_id: (r.user_id as string | null) ?? null,
    surface: (r.surface as string | null) ?? null,
    target_date: (r.target_date as string | null) ?? null,
    audio_path: (r.audio_path as string | null) ?? null,
    duration_sec: typeof r.duration_sec === 'number' ? r.duration_sec : null,
    chapters: normaliseChapters(r.chapters),
    voice: (r.voice as string | null) ?? null,
    vendor: (r.vendor as string | null) ?? null,
    region: (r.region as string | null) ?? null,
    generated_at: (r.generated_at as string | null) ?? null,
  };
}

// Throw-on-error fetcher. useCachedQuery requires the queryFn to THROW so the cached placeholder is
// served offline (and so the error path stays catchable). Returns ALL of the caller's episodes for their
// LATEST available target_date (one per surface, incl comprehensive), ordered by the preferred surface
// order. Returns [] when the table is empty (a valid, non-error state — the page renders an empty state).
async function fetchLatestEpisodes(): Promise<BriefEpisode[]> {
  // `research_brief_episodes` (M14) is not in the generated types yet, so the table literal itself is
  // rejected by `.from()`'s overloads — cast `supabase` to `any` (not just the return) so the literal
  // passes. The orchestrator regenerates types.ts separately; switch back to the typed call then.
  //
  // Scope to the CALLER explicitly. RLS already returns only own rows for a normal user, but for an
  // admin/superadmin the USING expr (`user_id = auth.uid() OR is_admin_or_superadmin(auth.uid())`)
  // returns ALL users' rows — so an unscoped query would yield other users' episodes. Filtering by
  // user_id enforces "own episodes" for every role and keeps the cache key semantically "this user's
  // brief". We then keep only the rows for the single most-recent target_date (one per surface).
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth?.user?.id ?? null;

  let q = (supabase as any)
    .from('research_brief_episodes')
    .select(
      'id, user_id, surface, target_date, audio_path, duration_sec, chapters, voice, vendor, region, generated_at',
    );
  if (uid) q = q.eq('user_id', uid);

  // Order by target_date desc first so the latest day is at the top; generated_at desc as a tiebreak so,
  // within a (date, surface), the freshest re-generation wins. We pull a small bounded page (enough to
  // cover all surfaces for the latest day) and filter to the latest target_date client-side.
  const { data, error } = await q
    .order('target_date', { ascending: false })
    .order('generated_at', { ascending: false })
    .limit(40);

  if (error) throw error;

  const rows = (Array.isArray(data) ? data : []) as Record<string, unknown>[];
  if (rows.length === 0) return [];

  // Latest available target_date (rows are already date-desc ordered).
  const latestDate = (rows[0].target_date as string | null) ?? null;

  // Keep only the latest day, one episode per surface (first wins = freshest by generated_at).
  const bySurface = new Map<string, BriefEpisode>();
  for (const r of rows) {
    const rowDate = (r.target_date as string | null) ?? null;
    if (rowDate !== latestDate) continue;
    const surfaceKey = ((r.surface as string | null) ?? '').toLowerCase();
    if (bySurface.has(surfaceKey)) continue; // first row for this surface is the freshest
    bySurface.set(surfaceKey, mapEpisode(r));
  }

  // Order the surfaces: comprehensive → cio → pm → analyst → (anything else, by generated_at desc).
  return Array.from(bySurface.values()).sort((a, b) => {
    const ra = surfaceRank((a.surface ?? '').toLowerCase());
    const rb = surfaceRank((b.surface ?? '').toLowerCase());
    if (ra !== rb) return ra - rb;
    // Stable-ish secondary order for unknown surfaces.
    return (b.generated_at ?? '').localeCompare(a.generated_at ?? '');
  });
}

// ---------------------------------------------------------------------------
// Native pre-download (best-effort, web no-op). Never throws into the hook.
// ---------------------------------------------------------------------------

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// Download the signed-URL bytes and stash them in the app-sandboxed Cache dir so the brief plays
// offline. Returns the on-device file URI (a `capacitor://`/`file://`-style src usable by <audio>) or
// null when not native / on any failure (caller falls back to the signed URL). Mirrors the
// offlineQueue writeBlob idiom (dynamic import behind isNative(), try/catch, Filesystem + Directory).
async function predownloadNative(signedUrl: string): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const res = await fetch(signedUrl);
    if (!res.ok) return null;
    const blob = await res.blob();
    const b64 = await blobToBase64(blob);

    await Filesystem.mkdir({
      path: BRIEF_DIR,
      directory: Directory.Cache,
      recursive: true,
    }).catch(() => {
      /* already exists */
    });
    await Filesystem.writeFile({
      path: BRIEF_FILE,
      data: b64,
      directory: Directory.Cache,
    });
    const { uri } = await Filesystem.getUri({
      path: BRIEF_FILE,
      directory: Directory.Cache,
    });
    // Convert to a webview-playable src. On native, Capacitor.convertFileSrc maps the file:// uri to a
    // loadable scheme; if the helper is unavailable, fall back to the raw uri.
    try {
      const { Capacitor } = await import('@capacitor/core');
      return Capacitor.convertFileSrc(uri);
    } catch {
      return uri;
    }
  } catch {
    /* plugin/IO unavailable: stay fail-open — playback uses the signed URL */
    return null;
  }
}

// Probe for an already-downloaded brief file (native only) without re-fetching. Used OFFLINE, when
// signing the URL fails: a brief downloaded on a previous online run may still be on disk, so we can
// still play it. Returns a webview-playable src or null.
//
// CAVEAT (documented): there is no persisted episode-id ↔ file mapping, so the on-disk file could in
// principle belong to a PREVIOUS episode (the single-file path is overwritten on each fresh download).
// In practice the file tracks the latest episode; gating replay on a stored lastDownloadedEpisodeId
// would make this exact — left as a deliberate, low-risk simplification for this slice.
async function probeDownloadedNative(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    // stat throws if the file is absent → caught below, returns null.
    await Filesystem.stat({ path: BRIEF_FILE, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path: BRIEF_FILE, directory: Directory.Cache });
    try {
      const { Capacitor } = await import('@capacitor/core');
      return Capacitor.convertFileSrc(uri);
    } catch {
      return uri;
    }
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// AUD-5 — native foreground-service bridge (Android background / screen-locked playback).
//
// The actual audio is decoded by the WebView's HTMLAudioElement; this just promotes the app to a
// foreground media service so the OS keeps it alive while backgrounded / locked. All calls are gated
// behind isNative(), dynamic-import the plugin, and swallow every error — on web (or any platform where
// the plugin is absent) they are pure no-ops, so the public bundle and existing behaviour are untouched.
// The plugin is registered against the native @CapacitorPlugin name "BriefAudioService".
// ---------------------------------------------------------------------------

interface BriefAudioServicePlugin {
  start(options: { title?: string }): Promise<void>;
  stop(): Promise<void>;
  update(options: { title?: string }): Promise<void>;
}

async function getBriefAudioPlugin(): Promise<BriefAudioServicePlugin | null> {
  if (!isNative()) return null;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    // registerPlugin returns a proxy even if the native side is missing; the no-op try/catch at the
    // call sites means a missing implementation simply rejects and is swallowed.
    return registerPlugin<BriefAudioServicePlugin>('BriefAudioService');
  } catch {
    return null;
  }
}

// Start the foreground media service (native only, best-effort).
async function startNativeAudioService(title: string): Promise<void> {
  try {
    const plugin = await getBriefAudioPlugin();
    if (!plugin) return;
    await plugin.start({ title });
  } catch {
    /* plugin/native unavailable — playback still works via the WebView while the app is foregrounded */
  }
}

// Stop the foreground media service (native only, best-effort).
async function stopNativeAudioService(): Promise<void> {
  try {
    const plugin = await getBriefAudioPlugin();
    if (!plugin) return;
    await plugin.stop();
  } catch {
    /* best-effort */
  }
}

/**
 * Remove the pre-downloaded brief audio + the cached episode envelope. Best-effort, never throws.
 * Exported so the sign-out / wipe path can purge confidential local state on logout.
 *
 * WIRING: call this from the existing sign-out purge path. The canonical confidential-wipe entry point
 * is `wipeLocalDevice()` (src/lib/native/wipe.ts) — see the TODO added at its purge sequence — and the
 * interactive sign-out is `useAuth().signOut()` (src/hooks/useAuth.ts:287) used by
 * `ProfileSheet.handleSignOut` (src/components/mobile/ProfileSheet.tsx:70). A TODO has been added at
 * both the wipe sequence and the ProfileSheet handler naming this call site.
 */
export async function purgeBriefAudio(): Promise<void> {
  // 1. Delete the downloaded file (native only).
  if (isNative()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      await Filesystem.deleteFile({ path: BRIEF_FILE, directory: Directory.Cache }).catch(() => {});
      await Filesystem.rmdir({
        path: BRIEF_DIR,
        directory: Directory.Cache,
        recursive: true,
      }).catch(() => {});
    } catch {
      /* plugin unavailable / already gone */
    }
  }
  // 2. Remove the cached episode envelope.
  try {
    await removeCached(CACHE_KEY);
  } catch {
    /* best-effort */
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseBriefAudio {
  /** All of the user's episodes for the latest available target_date (one per surface). */
  episodes: BriefEpisode[];
  /** The currently selected surface (e.g. 'comprehensive' | 'cio' | 'pm' | 'analyst'), or null. */
  selectedSurface: string | null;
  /** Change the selected surface; loads + plays that episode using the existing player. */
  setSelectedSurface: (surface: string) => void;
  episode: BriefEpisode | null;
  chapters: BriefChapter[];
  signedUrl: string | null;
  playing: boolean;
  currentTime: number;
  duration: number;
  rate: number;
  isFromCache: boolean;
  cachedAt: string | null;
  loading: boolean;
  downloaded: boolean;
  error: string | null;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (sec: number) => void;
  setRate: (r: number) => void;
  skipToChapter: (i: number) => void;
}

export function useBriefAudio(): UseBriefAudio {
  const query = useCachedQuery<BriefEpisode[]>({
    queryKey: QUERY_KEY as unknown as readonly unknown[],
    cacheKey: CACHE_KEY,
    queryFn: fetchLatestEpisodes,
  });

  // Cached value may be null on first paint; default to an empty list. Old caches held a single object
  // rather than an array — coerce that legacy shape to a one-element list so the page never crashes.
  const episodes = useMemo<BriefEpisode[]>(() => {
    const d = query.data as unknown;
    if (Array.isArray(d)) return d as BriefEpisode[];
    if (d && typeof d === 'object') return [d as BriefEpisode];
    return [];
  }, [query.data]);

  // Selected surface (null until episodes load). Default to 'comprehensive' when present, else the first
  // available in the preferred order (already sorted by fetchLatestEpisodes).
  const [selectedSurface, setSelectedSurfaceState] = useState<string | null>(null);

  // Resolve the default / keep a valid selection as the episode list changes (e.g. cache → network).
  useEffect(() => {
    if (episodes.length === 0) {
      setSelectedSurfaceState(null);
      return;
    }
    const surfaces = episodes.map((e) => (e.surface ?? '').toLowerCase());
    setSelectedSurfaceState((prev) => {
      // Keep the current selection if it still exists in the new list.
      if (prev && surfaces.includes(prev)) return prev;
      if (surfaces.includes('comprehensive')) return 'comprehensive';
      return surfaces[0] ?? null;
    });
  }, [episodes]);

  const setSelectedSurface = useCallback((surface: string) => {
    setSelectedSurfaceState(surface.toLowerCase());
  }, []);

  // The selected episode drives all the existing playback wiring below.
  const episode = useMemo<BriefEpisode | null>(() => {
    if (episodes.length === 0) return null;
    const match = episodes.find((e) => (e.surface ?? '').toLowerCase() === selectedSurface);
    return match ?? episodes[0];
  }, [episodes, selectedSurface]);

  const chapters = useMemo(() => episode?.chapters ?? [], [episode]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Lazily construct the single shared element (browser-only).
  if (audioRef.current == null && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
  }

  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRateState] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // ---- Resolve playback source whenever the episode changes -------------------------------------
  // Resolve a signed URL for the private audio, then (native only) pre-download for offline play. The
  // <audio> src prefers the local file when the download succeeds, else the signed URL.
  useEffect(() => {
    let cancelled = false;
    setSignedUrl(null);
    setDownloaded(false);
    setError(null);

    const path = episode?.audio_path;
    if (!path) return;

    (async () => {
      let url: string | null = null;
      try {
        const { data, error: signErr } = await supabase.storage
          .from(STORAGE_BUCKET)
          .createSignedUrl(path, 3600);
        if (signErr) throw signErr;
        url = data?.signedUrl ?? null;
      } catch {
        // Signing failed (offline / permission). On native a prior download may still let us play; if
        // not, surface a gentle note rather than a hard error.
        url = null;
      }
      if (cancelled) return;
      if (url) setSignedUrl(url);

      if (url) {
        // ONLINE path: native pre-download for offline playback. Fetches the bytes via the signed URL
        // and stashes them on disk so the brief plays offline later.
        const localSrc = await predownloadNative(url);
        if (cancelled) return;
        if (localSrc) {
          setDownloaded(true);
          const el = audioRef.current;
          if (el && el.src !== localSrc) {
            el.src = localSrc;
          }
        }
      } else {
        // OFFLINE path (signing failed): a previous online run may have left the audio on disk. Probe
        // for it (native only) and bind it so a brief downloaded earlier still plays offline — rather
        // than showing "not downloaded for offline" when the bytes are actually present.
        const localSrc = await probeDownloadedNative();
        if (cancelled) return;
        if (localSrc) {
          setDownloaded(true);
          const el = audioRef.current;
          if (el && el.src !== localSrc) {
            el.src = localSrc;
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [episode?.id, episode?.audio_path]);

  // ---- Bind the <audio> element src (prefer downloaded local file, else signed URL) --------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    // If we have a downloaded local src already set, leave it; otherwise use the signed URL.
    if (!downloaded && signedUrl && el.src !== signedUrl) {
      el.src = signedUrl;
    }
  }, [signedUrl, downloaded]);

  // ---- Audio element event wiring ----------------------------------------------------------------
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const onTime = () => setCurrentTime(el.currentTime || 0);
    const onDuration = () => {
      const d = el.duration;
      setDuration(Number.isFinite(d) ? d : episode?.duration_sec ?? 0);
    };
    const serviceTitle = (() => {
      const dateLabel = episode?.target_date
        ? new Date(episode.target_date).toLocaleDateString([], {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : 'Daily brief';
      return `Audio brief — ${dateLabel}`;
    })();

    const onPlay = () => {
      setPlaying(true);
      // AUD-5: keep the WebView alive for background / screen-locked playback (native only, no-op on web).
      void startNativeAudioService(serviceTitle);
    };
    const onPause = () => {
      setPlaying(false);
      void stopNativeAudioService();
    };
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
      void stopNativeAudioService();
    };
    const onError = () => setError('We couldn’t play this brief. Try again when you’re back online.');

    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onDuration);
    el.addEventListener('durationchange', onDuration);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('error', onError);

    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onDuration);
      el.removeEventListener('durationchange', onDuration);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('error', onError);
    };
  }, [episode?.duration_sec, episode?.target_date]);

  // Seed duration from the episode metadata before the element loads (so the bar isn't zero-width).
  useEffect(() => {
    if (episode?.duration_sec && duration === 0) setDuration(episode.duration_sec);
  }, [episode?.duration_sec, duration]);

  // Stop + reset playback when the episode goes away (e.g. cache cleared) so we never play a stale src.
  // Also DROP the element's source: on a security-sensitive logout / cache purge we must not leave a
  // signed-URL or file:// reference live on the shared element.
  useEffect(() => {
    if (!episode) {
      const el = audioRef.current;
      if (el) {
        try {
          el.pause();
          el.removeAttribute('src');
          el.load();
        } catch {
          /* ignore */
        }
      }
      setPlaying(false);
      setCurrentTime(0);
    }
  }, [episode]);

  // ---- Controls ----------------------------------------------------------------------------------
  const play = useCallback(() => {
    const el = audioRef.current;
    if (!el || !el.src) return;
    void el.play().catch(() => {
      setError('We couldn’t play this brief. Try again when you’re back online.');
    });
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) play();
    else el.pause();
  }, [play]);

  const seek = useCallback((sec: number) => {
    const el = audioRef.current;
    if (!el) return;
    const clamped = Math.max(0, Number.isFinite(sec) ? sec : 0);
    try {
      el.currentTime = clamped;
      setCurrentTime(clamped);
    } catch {
      /* seeking before metadata is loaded — ignore */
    }
  }, []);

  const setRate = useCallback((r: number) => {
    const el = audioRef.current;
    setRateState(r);
    if (el) el.playbackRate = r;
  }, []);

  const skipToChapter = useCallback(
    (i: number) => {
      const c = chapters[i];
      if (!c) return;
      seek(c.start_sec);
      // Skipping a chapter is an intent to listen — start playback if paused.
      if (audioRef.current?.paused) play();
    },
    [chapters, seek, play],
  );

  // Keep playbackRate applied across src swaps.
  useEffect(() => {
    const el = audioRef.current;
    if (el) el.playbackRate = rate;
  }, [rate, signedUrl, downloaded]);

  // ---- Pause on unmount (don't keep audio playing after leaving the page) ------------------------
  useEffect(() => {
    return () => {
      try {
        audioRef.current?.pause();
      } catch {
        /* ignore */
      }
      // AUD-5: tear down the foreground media service so it never outlives the player (native no-op on web).
      void stopNativeAudioService();
    };
  }, []);

  // ---- navigator.mediaSession (Android lock-screen transport while the process is alive) ---------
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    if (!episode) return;

    try {
      const dateLabel = episode.target_date
        ? new Date(episode.target_date).toLocaleDateString([], {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : 'Daily brief';
      const surface = episode.surface ?? 'Brief';
      // MediaMetadata may not exist in every webview; guard its construction.
      if (typeof MediaMetadata !== 'undefined') {
        ms.metadata = new MediaMetadata({
          title: `Audio brief — ${dateLabel}`,
          artist: surface,
          album: 'CBAM research',
        });
      }
    } catch {
      /* metadata best-effort */
    }

    const setHandler = (
      action: MediaSessionAction,
      handler: MediaSessionActionHandler | null,
    ) => {
      try {
        ms.setActionHandler(action, handler);
      } catch {
        /* unsupported action on this platform — ignore */
      }
    };

    const currentChapterIndex = (): number => {
      let idx = -1;
      for (let i = 0; i < chapters.length; i++) {
        if (chapters[i].start_sec <= (audioRef.current?.currentTime ?? 0)) idx = i;
        else break;
      }
      return idx;
    };

    setHandler('play', () => play());
    setHandler('pause', () => pause());
    setHandler('seekbackward', (d) => seek((audioRef.current?.currentTime ?? 0) - (d.seekOffset ?? 15)));
    setHandler('seekforward', (d) => seek((audioRef.current?.currentTime ?? 0) + (d.seekOffset ?? 15)));
    setHandler('previoustrack', () => {
      const idx = currentChapterIndex();
      // Within the first 3s of a chapter (or before any), go to the previous chapter; else restart this one.
      const cur = audioRef.current?.currentTime ?? 0;
      const here = idx >= 0 ? chapters[idx].start_sec : 0;
      if (idx > 0 && cur - here < 3) skipToChapter(idx - 1);
      else if (idx >= 0) seek(here);
      else seek(0);
    });
    setHandler('nexttrack', () => {
      const idx = currentChapterIndex();
      if (idx + 1 < chapters.length) skipToChapter(idx + 1);
    });

    return () => {
      // Clear handlers so a later page doesn't inherit ours.
      for (const a of [
        'play',
        'pause',
        'seekbackward',
        'seekforward',
        'previoustrack',
        'nexttrack',
      ] as MediaSessionAction[]) {
        setHandler(a, null);
      }
    };
  }, [episode, chapters, play, pause, seek, skipToChapter]);

  // Reflect playback state to the OS so the lock-screen control shows the right play/pause glyph.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
    } catch {
      /* ignore */
    }
  }, [playing]);

  return {
    episodes,
    selectedSurface,
    setSelectedSurface,
    episode,
    chapters,
    signedUrl,
    playing,
    currentTime,
    duration,
    rate,
    isFromCache: query.isFromCache,
    cachedAt: query.cachedAt,
    loading: query.isLoading,
    downloaded,
    error,
    play,
    pause,
    toggle,
    seek,
    setRate,
    skipToChapter,
  };
}
